import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router";
import {
  Box,
  Paper,
  TextField,
  CircularProgress,
  TableContainer,
  Button,
  Table,
  AlertTitle,
  Alert,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableCell,
  AppBar,
  Toolbar,
  IconButton,
  Fade,
  Slide,
  Zoom,
  Grow,
  alpha,
  useTheme,
  styled,
  tableCellClasses,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import axios from "axios";
import { baseUrl, config, requestID } from "../../common/utils";
import styles from "./ApproveMembership.module.css";
import { IonButtons, IonContent, IonHeader, IonMenuButton, IonPage, IonTitle, IonToolbar } from "@ionic/react";

interface User {
  user_id: string;
  fullName: string;
  email: string;
  phone: string;
  loanAmount: number;
  monthlyRepay: number;
  loanDuration: number;
  expected_guarantors: number;
  guarantee_log: number;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    // backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    transition: 'background-color 0.3s ease',
  },
}));

const ApproveMembership: React.FC = () => {
  //   const { name } = useParams<{ name: string }>();
  const location = useLocation();
  const name = location.pathname.split("/").pop()?.replace(/%20/g, " ") || "Approve Membership";
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState({ loading: false, error: false });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [actionType, setActionType] = useState<"approve" | "reject" | null>(null);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertMsg, setAlertMsg] = useState<string>("");
  const theme = useTheme();

  const onShowAlert = () => {
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 5000);
  };

  const getAllUnApprovedRequest = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(
        baseUrl() + "users/getAllUnApprovedRequest",
        formData,
        config
      );
      const { data } = response.data;
      setUsers(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching users:", error);
      setLoading(false);
    }
  };

  const filteredUsers = users?.filter(
    (user) =>
      user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user?.phone?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleApprove = async (userId: string) => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("user_id", userId);

      const response = await axios.post(
        baseUrl() + "users/approveMember",
        formData,
        config
      );
      if (response.data.status == true) {
        getAllUnApprovedRequest();
        setAlertMsg(response.data.message);
        onShowAlert();
      } else {
        setAlertMsg(response.data.message);
        onShowAlert();
      }
    } catch (error) {
      console.error("Error approving loan:", error);
    }
  };

  const handleReject = async (userId: string) => {
    try {
      // Implement reject functionality
      console.log("Reject user:", userId);
    } catch (error) {
      console.error("Error rejecting loan:", error);
    }
  };

  const handleOpenDialog = (userId: string, action: "approve" | "reject") => {
    setSelectedUserId(userId);
    setActionType(action);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUserId(null);
    setActionType(null);
  };

  const handleConfirmAction = () => {
    if (actionType && selectedUserId) {
      if (actionType === "approve") {
        handleApprove(selectedUserId);
      } else if (actionType === "reject") {
        handleReject(selectedUserId);
      }
      handleCloseDialog();
    }
  };

  useEffect(() => {
    getAllUnApprovedRequest();
  }, []);

  return (
    <IonPage>
      <IonHeader className={styles.appBar}>
                <IonToolbar className={styles.headerToolbar}>
                  <IonButtons slot="start">
                    <IonMenuButton />
                  </IonButtons>
          <IonTitle className={styles.headerTitle}>
            {name}
          </IonTitle>
                </IonToolbar>
              </IonHeader>
      <IonContent>
        {/* <Box className={styles.pageContainer}> */}
      {/* <AppBar position="static" elevation={2} className={styles.appBar}>
        <Toolbar>
          <Typography variant="h6" component="div" className={styles.title}>
            {name}
          </Typography>
        </Toolbar>
      </AppBar> */}

      {/* <Box className={styles.content}> */}
        <Slide direction="down" in={showAlert} mountOnEnter unmountOnExit>
          <Alert 
            severity={status.error ? "error" : "success"} 
            className={styles.alert}
            sx={{ mb: 2 }}
          >
            <AlertTitle>{status.error ? "Error" : "Success"}</AlertTitle>
            {alertMsg?.toUpperCase()}
          </Alert>
        </Slide>

        {/* <Grow in={!loading} timeout={500}> */}
          <Paper elevation={0} className={styles.paper}>
            <Box className={styles.searchContainer}>
              <SearchIcon className={styles.searchIcon} />
              <TextField
                variant="outlined"
                fullWidth
                placeholder="Search by Email or Phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchField}
                InputProps={{
                  className: styles.searchInput,
                }}
              />
            </Box>

            {loading ? (
              <Box className={styles.loadingContainer}>
                <CircularProgress size={60} thickness={4} className={styles.spinner} />
                <Typography variant="h6" className={styles.loadingText}>
                  Loading members...
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} elevation={0} className={styles.tableContainer}>
                  <Table className={styles.table}>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>S/N</StyledTableCell>
                        <StyledTableCell>Name</StyledTableCell>
                        <StyledTableCell>Email</StyledTableCell>
                        <StyledTableCell>Phone</StyledTableCell>
                        <StyledTableCell>Loan Amount</StyledTableCell>
                        <StyledTableCell>Monthly Repayment</StyledTableCell>
                        <StyledTableCell>Duration</StyledTableCell>
                        <StyledTableCell>Actions</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredUsers
                        ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((user, i) => (
                          <Zoom in={true} timeout={300 + (i * 100)} key={i}>
                            <StyledTableRow className={styles.tableRow}>
                              <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                              <TableCell>{user.fullName}</TableCell>
                              <TableCell>{user.email}</TableCell>
                              <TableCell>{user.phone}</TableCell>
                              <TableCell>${user.loanAmount?.toLocaleString()}</TableCell>
                              <TableCell>${user.monthlyRepay?.toLocaleString()}</TableCell>
                              <TableCell>{user.loanDuration} months</TableCell>
                              <TableCell>
                                <Box className={styles.actionButtons}>
                                  <Button
                                    variant="contained"
                                    disabled={user?.expected_guarantors !== user?.guarantee_log}
                                    color="success"
                                    onClick={() => handleOpenDialog(user.user_id, "approve")}
                                    className={styles.approveButton}
                                    startIcon={<CheckCircleIcon />}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => handleOpenDialog(user.user_id, "reject")}
                                    className={styles.rejectButton}
                                    startIcon={<CancelIcon />}
                                  >
                                    Reject
                                  </Button>
                                </Box>
                              </TableCell>
                            </StyledTableRow>
                          </Zoom>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={filteredUsers?.length ?? 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  className={styles.pagination}
                />
              </>
            )}
          </Paper>
        {/* </Grow> */}

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          aria-labelledby="confirmation-dialog-title"
          PaperProps={{ className: styles.dialogPaper }}
          TransitionComponent={Fade}
        >
          <DialogTitle id="confirmation-dialog-title" className={styles.dialogTitle}>
            Confirm Action
          </DialogTitle>
          <DialogContent className={styles.dialogContent}>
            <Typography variant="body1" className={styles.dialogText}>
              Are you sure you want to {actionType} this Member?
            </Typography>
          </DialogContent>
          <DialogActions className={styles.dialogActions}>
            <Button 
              onClick={handleCloseDialog} 
              className={styles.cancelButton}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAction}
              color={actionType === "approve" ? "success" : "error"}
              className={actionType === "approve" ? styles.confirmApproveButton : styles.confirmRejectButton}
              variant="contained"
            >
              {actionType === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogActions>
        </Dialog>
      {/* </Box> */}
      {/* </Box> */}
    </IonContent>
      </IonPage>
  );
};

export default ApproveMembership;